import {useMemo} from 'react'
import {InlineRow} from '@/components/shared/InlineRow'
import {useQuery} from '@tanstack/react-query'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {Plus} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {useServices} from '@/services/context'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {Icon} from '@/components/shared/Icon'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {Pagination} from '@/components/ui/pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {useServerPagination} from '@/hooks/use-server-pagination'
import {ApprovalPolicyControllerPoliciesEndpoint} from '@/types/endpoint-contracts'

export function ApprovalPolicies() {
    const i18n = useI18n()
    const services = useServices()
    usePageTitle(i18n.approvalPolicies.pageTitle)
    const navigate = useNavigate()
    const {hasPermission} = useAuth()
    const canCreate = hasPermission(permissions.approvalPolicies.create)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: ApprovalPolicyControllerPoliciesEndpoint,
        defaultSortBy: ApprovalPolicyControllerPoliciesEndpoint.sort.PRIORITY,
    })

    const {data: policiesPage} = useQuery({
        queryKey: ['approval-policies', queryString, page, pageSize],
        queryFn: () => services.approvalPolicies.list(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const policies = policiesPage?.content || []
    const totalItems = policiesPage?.totalElements || 0
    const totalPages = policiesPage?.totalPages || 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.approvalPolicy}
            title={i18n.approvalPolicies.allPolicies}
            count={totalItems}
            description={`${i18n.approvalPolicies.policiesCount({count: totalItems})}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="approval-policies-list"
            actions={canCreate ? (
                <Button onClick={() => navigate(routes.accessGovernance.approvalPolicies.create())} data-testid="btn-create-policy">
                    <Icon icon={Plus} className="mr-2"/>
                    {i18n.approvalPolicies.createPolicy}
                </Button>
            ) : undefined}
            filters={
                <UnifiedSearchBar
                    contract={ApprovalPolicyControllerPoliciesEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">{i18n.approvalPolicies.name}</TableHead>
                        <TableHead data-testid="col-policy-type">{i18n.approvalPolicies.policyType}</TableHead>
                        <TableHead data-testid="col-priority">{i18n.approvalPolicies.priority}</TableHead>
                        <TableHead data-testid="col-conditions">{i18n.approvalPolicies.conditions}</TableHead>
                        <TableHead data-testid="col-steps">{i18n.approvalPolicies.steps}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {policies.map((policy) => (
                        <ClickableTableRow
                            key={policy.id}
                            to={routes.accessGovernance.approvalPolicies.detail(policy.id)}
                            data-testid={`policy-row-${policy.id}`}
                        >
                            <TableCell className="font-medium">
                                <InlineRow>
                                    <TextLink
                                        to={routes.accessGovernance.approvalPolicies.detail(policy.id)}
                                        stopPropagation
                                        testId={`link-policy-${policy.id}`}
                                    >
                                        {policy.name}
                                    </TextLink>
                                    {policy.isDefault && (
                                        <Badge variant="secondary">{i18n.approvalPolicies.defaultBadge}</Badge>
                                    )}
                                </InlineRow>
                                {policy.description && (
                                    <MutedText size="sm" className="block">
                                        {policy.description}
                                    </MutedText>
                                )}
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline" data-testid={`badge-policy-type-${policy.id}`}>
                                    {i18n.approvalPolicies.policyTypeLabels[policy.policyType]}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline">{policy.priority}</Badge>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline">
                                    {i18n.approvalPolicies.conditionsCount({count: policy.conditions.length})}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline">
                                    {i18n.approvalPolicies.stepsCount({count: policy.steps.length})}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                <Badge variant={policy.active ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}>
                                    {policy.active ? i18n.common.active : i18n.common.inactive}
                                </Badge>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {policies.length === 0 && (
                        <TableEmptyState
                            colSpan={6}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.approvalPolicies.noPoliciesFound}
                            emptyMessage={i18n.approvalPolicies.noPoliciesFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
